<template>
    <FormKit
        type="form"
        :form-class="isFormSubmitted ? 'hide' : 'show'"
        @submit="submitHandler"
        :actions="false"
        #default="{ state: { valid } }"
    >
        <div class="card">
            <DataTable lazy dataKey="id"
                stateStorage="local"
                ref="dt"
                :state-key="tableLocalStorageKey"
                :value="tableData"
                :paginator="true"
                :rows="rows"
                :rowHover="true"
                sortMode="multiple"
                removableSort
                v-model:selection="selectedBrands"
                :metaKeySelection="false"
                :loading="isLoading"
                :totalRecords="totalRecords"
                :currentPage="currentPage"
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                :rowsPerPageOptions="rowsPerPageOptions"
                currentPageReportTemplate="Affichage de {first} à {last} sur {totalRecords} entrées"
                :selectAll="isSelectAllBtnChecked"
                @select-all-change="onRowSelectAll($event)"
                @row-select="onRowSelect($event)"
                @row-unselect="onRowUnselect($event)"
                @page="onPaginate($event)"
            >
            <template #header>
                <div class="flex flex-row flex-nowrap card-header border-0">
                    <div class="flex card-toolbar w-full">
                        <div class="w-full">
                            <span class="p-input-icon-left w-10/12">
                                <i class="pi pi-search" />
                                <InputText type="text" class="w-9/12" v-model="criteria" @keydown.enter.prevent="onSearchInputDt()" placeholder="Rechercher par marque" />
                            </span>
                        </div>
                    </div>
                    <div class="card-toolbar w-full">
                        <div class="flex flex-row items-center justify-between">
                            <div class="flex flex-col">
                                <p>Nombre total de marques : {{ totalRecords }}</p>
                                <p>Nombre de marque(s) sélectionnée(s) : {{ totalSelectedBrands }} marque(s)</p>
                                <p v-if="isInFilteredView">Vous êtes en vue filtrée</p>
                            </div>
                            <div class="flex flex-col ml-4">
                                <Dropdown
                                    v-model="selectedSupplier"
                                    :options="uniqueSuppliers"
                                    optionLabel="name"
                                    placeholder="Sélectionner un fournisseur"
                                    filter
                                    :showClear="true"
                                    class="w-64"
                                    @change="onSupplierFilterChange"
                                >
                                    <template #value="slotProps">
                                        <div v-if="slotProps.value">
                                            {{ slotProps.value.name }}
                                        </div>
                                        <span v-else>Sélectionner un fournisseur</span>
                                    </template>
                                    <template #option="slotProps">
                                        <div>{{ slotProps.option.name }}</div>
                                    </template>
                                </Dropdown>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
            <div class="card-body pt-0">
                <Column selectionMode="multiple"></Column>
                <Column field="brand" header="Marque">
                <template #body="slotProps">
                    <span>{{ slotProps.data.brand }}</span>
                </template>
                </Column>
                <Column field="supplier" header="Fournisseur">
                <template #body="slotProps">
                    <span>{{ slotProps.data.supplier }}</span>
                </template>
                </Column>
            </div>
            <template #empty>Aucun résultat trouvé.</template>
        </DataTable>
    </div>
    <div class="ml-96 mt-10">
        <FormKit type="submit" label="Enregistrer" :disabled="!valid" />
    </div>
    </FormKit>
</template>

<script lang="ts" setup>
import {computed, onBeforeMount, onMounted, ref, watch} from "vue";
import swal from "@/_helpers/swal";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import InputText from "primevue/inputtext";
import router from "@/router";
import ApiService from "@/_services/api.service";
import { useRoute } from 'vue-router';
import { categoryService } from "@/_services/category/category";
import { SocialEventBrands } from "@/models/product/brands/SocialEventBrands";
import Dropdown from "primevue/dropdown";

const tableLocalStorageKey = "tableSelectedBrandsToMap";
const selectedBrandToMapStorageKey = "selectedBrandsToMap";
const selectAllBtnStatusStorageKey = "selectAllBrandsForMappingBtnStatus";
const isSelectAllBtnChecked = ref<boolean>(false);
const refItemsPerPage = ref<number>(0);
const isLoading = ref<boolean>(true);
const brands = Array<SocialEventBrands>();
const tableData = ref<Array<SocialEventBrands>>(brands);
const allBrands = ref<Array<SocialEventBrands>>([]);
const originalBrands = ref<Array<SocialEventBrands>>([]);
const totalSelectedBrands = ref<number>(0);
const brandsOfCurrentPage = ref(Array<SocialEventBrands>());
const currentPage = ref<number>(0);
const rows = ref<number>(10);
const totalRecords = ref<number>(0);
const page = ref<number>(1);
const criteria = ref<string>("");
const defaultRowsPerPage = 10;
const rowsPerPageOptions = [defaultRowsPerPage, 25, 100, 250];
const isFormSubmitted = ref(false);
const selectedBrands = ref<SocialEventBrands[]>([]);
const category_model = ref<{uuid: string}>({uuid: ''});
const route = useRoute();
const selectedSupplier = ref<{name: string} | null>(null);
const uniqueSuppliers = ref<{name: string}[]>([]);

const preselectCategory = async () => {
    const categoryUuid = route.params.uuid as string;
    if (categoryUuid) {
        category_model.value = {
            uuid: categoryUuid
        };
    }
};

onBeforeMount(() => {
    initLocalStorageData();
});

onMounted(async () => {
    await preselectCategory();
    await populateDataTableWithLocalStorageFilters();
});

watch(isSelectAllBtnChecked, () => {
    setIsSelectedAllBtnStatusLocalStorage(isSelectAllBtnChecked.value);
});

const isInFilteredView = computed(() => {
    return totalSelectedBrands.value > 0;
});

const initLocalStorageData = () => {
    if (localStorage.getItem(selectAllBtnStatusStorageKey)) {
        localStorage.removeItem(selectAllBtnStatusStorageKey);
    }
    if (localStorage.getItem(tableLocalStorageKey)) {
        localStorage.removeItem(tableLocalStorageKey);
    }
    if (localStorage.getItem(selectedBrandToMapStorageKey)) {
        localStorage.removeItem(selectedBrandToMapStorageKey);
    }

    if (!localStorage.getItem(selectAllBtnStatusStorageKey)) {
        localStorage.setItem(selectAllBtnStatusStorageKey, 'false');
    }
    if (localStorage.getItem(tableLocalStorageKey)) {
        localStorage.removeItem(tableLocalStorageKey);
    }
    if (!localStorage.getItem(selectedBrandToMapStorageKey)) {
        localStorage.setItem(selectedBrandToMapStorageKey, '[]');
    }
}

function createBrandIdentifier(brand: SocialEventBrands): string {
    return `${brand.brand}__${brand.supplier}`;
}

async function submitHandler() {
    const selectedBrandIds = getSelectedBrandsToMapFromStorage();
    
    const selectedBrandNames = selectedBrandIds.map(brandId => {
        const brand = findBrandById(brandId);
        return brand ? brand.brand : null;
    }).filter(Boolean) as string[];

    const payload = {
        socialEvent: "/api/social_events/" + category_model.value.uuid,
        brands: selectedBrandNames,
    };

    try {
        const response = await ApiService.post('social_event_brands', payload);
        
        swal.success('Succès', 'La(les) marque(s) a/ont été associée(s) avec succès à la catégorie URSSAF.');
        
        setTimeout(() => {
            const routeLocation = router.resolve({ 
                name: 'editMappingCategoryUrssafBrand', 
                params: { uuid: route.params.uuid } 
            });
            window.location.href = routeLocation.href;
        }, 1500);
    } catch (error: any) {
        
        if (error.response) {
            
            if (error.response.data && error.response.data.hasError) {
                const errors = error.response.data.errors;
                
                if (errors && errors.length > 0) {
                    const errorMessage = errors[0].message;
                    swal.error(errorMessage);
                } else {
                    swal.error('Une erreur est survenue lors de la tentative d\'association des marques.');
                }
            } else if (error.response.data && error.response.data.detail) {
                // Format d'erreur Symfony/API Platform
                swal.error(error.response.data.detail);
            } else if (error.response.data && error.response.data.message) {
                // Autre format possible
                swal.error(error.response.data.message);
            } else {
                swal.error(`Erreur du serveur: ${error.response.status}`);
            }
        } else if (error.request) {
            swal.error('Aucune réponse reçue du serveur. Veuillez vérifier votre connexion réseau.');
        } else {
            swal.error(`Erreur: ${error.message}`);
        }
    }
}

const setIsSelectedAllBtnStatusLocalStorage = (status: boolean) =>{
    isSelectAllBtnChecked.value = status;
    const isSelectedAllBtnInStorageData = localStorage.getItem(selectAllBtnStatusStorageKey);
    if (!isSelectedAllBtnInStorageData) {
        localStorage.setItem(selectAllBtnStatusStorageKey, status.toString());
    } else {
        const currentStatus = JSON.parse(isSelectedAllBtnInStorageData);
        if (currentStatus !== status) {
            localStorage.setItem(selectAllBtnStatusStorageKey, status.toString());
        }
    }
}

function onSearchInputDt() {
    let search = criteria.value.trim();
    if (0 === search.length || 3 <= search.length) {
        page.value = 1;
        applyFilters();
    }
}

async function populateDatatable() {
    isLoading.value = true;
    
    try {
        if (category_model.value.uuid) {
            const result = await categoryService.fetchSocialEventAllBrands(category_model.value.uuid);
            
            isLoading.value = result.isLoading.value;
            
            if (result.hasError.value) {
                swal.error(result.errorMessage.value || 'Erreur lors de la récupération des marques');
                originalBrands.value = [];
                allBrands.value = [];
                tableData.value = [];
                totalRecords.value = 0;
            } else {
                result.brands.value.forEach(brand => {
                    brand.id = `${brand.brand}__${brand.supplier}`;
                });
                originalBrands.value = result.brands.value;
                
                uniqueSuppliers.value = extractUniqueSuppliers(result.brands.value);
                
                applyFilters();
                
                const preSelectedBrands = result.brands.value.filter(brand => brand.isSelected);
                if (preSelectedBrands.length > 0) {
                    selectedBrands.value = preSelectedBrands;
                    totalSelectedBrands.value = preSelectedBrands.length;
                    
                    const preSelectedBrandIds = preSelectedBrands.map(brand => createBrandIdentifier(brand));
                    addSelectedBrandInBrandsToMapInStorage(preSelectedBrandIds);
                }
                
                isSelectAllBtnChecked.value = (tableData.value.length > 0) && 
                                                (tableData.value.every(brand => 
                                                selectedBrands.value.some(selected => 
                                                    createBrandIdentifier(selected) === createBrandIdentifier(brand))));
            }
        }
    } catch (error) {
        isLoading.value = false;
        swal.error('Une erreur est survenue lors de la récupération des marques.');
    }

    return [isLoading.value, totalRecords.value, tableData.value];
}

function applyPagination() {
    const startIndex = (page.value - 1) * rows.value;
    const endIndex = Math.min(startIndex + rows.value, allBrands.value.length);
    
    tableData.value = allBrands.value.slice(startIndex, endIndex);
    brandsOfCurrentPage.value = tableData.value;
}

async function populateDataTableWithLocalStorageFilters(itemsPerPage?: number, page?: number) {
    let localStorageRowPerPage = getLocalStorageRowsPerPage();
    let localStorageFisrtPageState = getLocalStorageFisrtPageState();
    let currentPage = page;
    if (!currentPage) {
        currentPage = localStorageFisrtPageState / localStorageRowPerPage + 1;
    }
    let currentItemsPerPage = itemsPerPage;
    if (!currentItemsPerPage) {
        currentItemsPerPage = localStorageRowPerPage;
    }

    await populateDatatable();
}

const onPaginate = async (e) => {
    showSpinner();
    rows.value = e.rows;
    currentPage.value = e.page;
    page.value = e.page + 1;
    setLocalStorageRowsPerPage(rows.value);

    applyPagination();
    
    const selectedBrandsIds = getSelectedBrandsToMapFromStorage();
    isSelectAllBtnChecked.value = brandsOfCurrentPage.value.length > 0 && 
        brandsOfCurrentPage.value.every(brand => 
            selectedBrandsIds.includes(createBrandIdentifier(brand))
        );
    
    hideSpinner();
};

const showSpinner = () => {
    isLoading.value = true;
}
const hideSpinner = () => {
    isLoading.value = false;
}

const onRowSelectAll = async (event): Promise<void> => {
    if (true === event.checked) {
        isSelectAllBtnChecked.value = true;
        selectedBrands.value = brandsOfCurrentPage.value;
        const brandsOfCurrentPageToSelect = brandsOfCurrentPage.value
            .filter((item) => !getSelectedBrandsToMapFromStorage().includes(createBrandIdentifier(item)))
            .map(item => createBrandIdentifier(item));
        if (brandsOfCurrentPageToSelect.length > 0) {
            addSelectedBrandInBrandsToMapInStorage(brandsOfCurrentPageToSelect);
            const currentBrandsToMapInStorage = getSelectedBrandsToMapFromStorage();
            let newBrandsToMapInStorage = currentBrandsToMapInStorage;
            brandsOfCurrentPageToSelect.forEach((brandId: string) => {
                if (!currentBrandsToMapInStorage.includes(brandId)) {
                    newBrandsToMapInStorage.push(brandId);
                }
            });
            totalSelectedBrands.value = newBrandsToMapInStorage.length;
        }
    } else {
        isSelectAllBtnChecked.value = false;
        const unSelectedBrandsOfCurrentPageToRemoveInStorage = brandsOfCurrentPage.value.map(item => createBrandIdentifier(item));
        removeUnSelectedBrandFromBrandsToMapInStorage(unSelectedBrandsOfCurrentPageToRemoveInStorage);

        const brandsToMapInStorage = getSelectedBrandsToMapFromStorage();
        let newBrandsToMapInStorage = brandsToMapInStorage.filter(brandId => !unSelectedBrandsOfCurrentPageToRemoveInStorage.includes(brandId));
        totalSelectedBrands.value = newBrandsToMapInStorage.length;
        selectedBrands.value = selectedBrands.value.filter((item) =>
            !brandsOfCurrentPage.value.map((brand) => createBrandIdentifier(brand)).includes(createBrandIdentifier(item))
        );

        hideSpinner();
    }
};

const onRowSelect = async (event: any): Promise<void> => {
    const selectedBrand = event.data;
    const selectedBrandId = createBrandIdentifier(selectedBrand);
    totalSelectedBrands.value++;
    addSelectedBrandInBrandsToMapInStorage([selectedBrandId]);
    const totalSelectedBrandsOnCurrentPage = getTotalUnSelectedBrandsOnCurrentPage();
    isSelectAllBtnChecked.value = (refItemsPerPage.value - totalSelectedBrandsOnCurrentPage) === refItemsPerPage.value;
}

const onRowUnselect = async (event: any): Promise<void> => {
    const unSelectedBrand = event.data;
    const unSelectedBrandId = createBrandIdentifier(unSelectedBrand);
    removeUnSelectedBrandFromBrandsToMapInStorage([unSelectedBrandId]);
    const brandsToMapInStorage = getSelectedBrandsToMapFromStorage();
    let newBrandsToMapInStorage = brandsToMapInStorage.filter(brandId => brandId !== unSelectedBrandId);
    totalSelectedBrands.value = newBrandsToMapInStorage.length;
    isSelectAllBtnChecked.value = false;

    selectedBrands.value = selectedBrands.value.filter(
        (item) => createBrandIdentifier(item) !== unSelectedBrandId
    );
}

const getSelectedBrandsToMapFromStorage = (): string[] => {
    const selectedBrandsToMap = localStorage.getItem(selectedBrandToMapStorageKey);
    return selectedBrandsToMap ? JSON.parse(selectedBrandsToMap) : [];
}

const addSelectedBrandInBrandsToMapInStorage = (brandIds: string[]) => {
    if (brandIds.length === 0) {
        totalSelectedBrands.value = 0;
        localStorage.setItem(selectedBrandToMapStorageKey, JSON.stringify([]));
        return;
    }

    let brandsToMapInStorage = getSelectedBrandsToMapFromStorage();
    brandIds.forEach(brandId => {
        if (!brandsToMapInStorage.includes(brandId)) {
            brandsToMapInStorage.push(brandId);
        }
    });
    localStorage.setItem(selectedBrandToMapStorageKey, JSON.stringify(brandsToMapInStorage));
}

const removeUnSelectedBrandFromBrandsToMapInStorage = (brandIds: string[]) => {
    let brandsToMapInStorage = getSelectedBrandsToMapFromStorage();
    let newBrandsToMapInStorage = brandsToMapInStorage.filter(brandId => !brandIds.includes(brandId));
    localStorage.setItem(selectedBrandToMapStorageKey, JSON.stringify(newBrandsToMapInStorage));
}

const getTotalUnSelectedBrandsOnCurrentPage = (): number => {
    return brandsOfCurrentPage.value
        .filter((item) => !getSelectedBrandsToMapFromStorage().includes(createBrandIdentifier(item))).length;
};

function getLocalStorageData() {
    let storageData = localStorage.getItem(tableLocalStorageKey);
    if (null !== storageData && "undefined" !== storageData) {
        return JSON.parse(storageData);
    }

    return null;
}

function setLocalStorageRowsPerPage(rowPerPage: number) {
    let storageData = localStorage.getItem(tableLocalStorageKey);
    if (storageData) {
        let localStorageObject = JSON.parse(storageData);
        localStorageObject.rows = rowPerPage;
        localStorage.setItem(tableLocalStorageKey, JSON.stringify(localStorageObject));
    }
}

function getLocalStorageRowsPerPage() {
    let localStorageData = getLocalStorageData();
    if (null !== localStorageData && localStorageData.rows) {
        return localStorageData.rows;
    }

    return defaultRowsPerPage;
}

function getLocalStorageFisrtPageState() {
    let localStorageData = getLocalStorageData();
    if (null !== localStorageData && localStorageData.first) {
        return localStorageData.first;
    }

    return 0;
}

function findBrandById(brandId: string): SocialEventBrands | undefined {
    const [brandName, supplierName] = brandId.split('__');
    return allBrands.value.find(brand => brand.brand === brandName && brand.supplier === supplierName);
}

function extractUniqueSuppliers(brands: SocialEventBrands[]): {name: string}[] {
    const supplierSet = new Set<string>();
    
    brands.forEach(brand => {
        if (brand.supplier) {
            supplierSet.add(brand.supplier);
        }
    });
    
    return Array.from(supplierSet).map(supplier => ({ name: supplier }));
}

function onSupplierFilterChange() {
    page.value = 1;
    applyFilters();
}

function applyFilters() {
    showSpinner();
    
    let filteredResults = [...originalBrands.value];
    
    if (criteria.value.trim() !== '') {
        filteredResults = filteredResults.filter(brand => {
            return brand.brand.toLowerCase().includes(criteria.value.toLowerCase()) || 
                    brand.supplier.toLowerCase().includes(criteria.value.toLowerCase());
        });
    }
    
    if (selectedSupplier.value) {
        filteredResults = filteredResults.filter(brand => 
            brand.supplier === selectedSupplier.value?.name
        );
    }
    
    allBrands.value = filteredResults;
    totalRecords.value = filteredResults.length;
    
    applyPagination();
    
    hideSpinner();
}
</script>
